import { Gender, StorySlug } from "@/api/episodes-types";
import { Prompt, PromptType } from "@/api/prompts-types";
import { useAllMasterCombinations } from "@/queries/master-combinations/use-master-combinations";
import { useStoryConfigs } from "@/queries/story-configs/use-story-configs";
import { pretty, prettyStoryType } from "@/utils/pretty";
import { storyCategories, storySubcategories } from "@/utils/story-categories";
import CloseIcon from "@mui/icons-material/Close";
import {
  IconButton,
  MenuItem,
  Paper,
  Stack,
  Switch,
  TextField,
  Typography,
} from "@mui/material";
import { ModelOption, StoryFormItemErrors, StoryFormItemValues } from "./types";

type StoryFormItemProps = {
  index: number;
  values: StoryFormItemValues;
  errors?: StoryFormItemErrors;
  promptsByStoryType: Record<string, Prompt[]>;
  onRemove: (index: number) => void;
  onChange: (
    index: number,
    field: keyof StoryFormItemValues,
    value: string | number | Gender | boolean | undefined,
  ) => void;
};

export function StoryFormItem({
  values,
  index,
  errors,
  promptsByStoryType,
  onRemove,
  onChange,
}: StoryFormItemProps) {
  const textPrompt =
    values.storyType &&
    promptsByStoryType[values.storyType].find(
      (p) => p.name === values.promptName && p.type === PromptType.text,
    );

  const imagePrompt =
    values.storyType &&
    promptsByStoryType[values.storyType].find(
      (p) => p.name === values.imagePromptName && p.type === PromptType.image,
    );

  const { data: configs } = useStoryConfigs();
  const { data: combinations } = useAllMasterCombinations();

  return (
    <Paper
      key={index}
      variant="outlined"
      sx={[
        {
          padding: 3,
          position: "relative",
          borderRadius: (theme) => theme.shape.borderRadius,
        },
      ]}
    >
      <IconButton
        onClick={() => onRemove(index)}
        sx={{ position: "absolute", top: 8, right: 8 }}
        size="small"
      >
        <CloseIcon />
      </IconButton>

      <Typography variant="h6" sx={{ mb: 2 }}>
        Story {index + 1}
      </Typography>

      <Stack gap={2}>
        <Stack direction="row" gap={2}>
          <StoryFormDropdown
            label="Story type*"
            value={values.storyType}
            options={Object.values(StorySlug).map((storyType) => ({
              value: storyType,
              label: prettyStoryType(storyType),
            }))}
            onChange={(value) => onChange(index, "storyType", value)}
            error={errors?.storyType}
          />

          <StoryFormDropdown
            label="Category*"
            value={values.category}
            options={storyCategories}
            onChange={(value) => onChange(index, "category", value)}
            error={errors?.category}
          />

          <StoryFormDropdown
            label="Subcategory*"
            value={values.subcategory}
            options={
              values.category
                ? storySubcategories[
                    values.category as keyof typeof storySubcategories
                  ] || []
                : []
            }
            onChange={(value) => onChange(index, "subcategory", value)}
            error={errors?.subcategory}
            disabled={!values.category}
          />

          <StoryFormDropdown
            label="Gender*"
            value={values.gender || ""}
            options={Object.values(Gender).map((gender) => ({
              value: gender,
              label: pretty(gender),
            }))}
            onChange={(value) => onChange(index, "gender", value)}
            error={errors?.gender}
          />
        </Stack>

        <Stack direction="row" gap={2}>
          <TextField
            label="Number of pages (excluding title)*"
            type="number"
            value={values.numberOfPages}
            onChange={(e) =>
              onChange(index, "numberOfPages", parseInt(e.target.value))
            }
            error={!!errors?.numberOfPages}
            helperText={errors?.numberOfPages}
            fullWidth
          />

          <TextField
            label="Page size*"
            value={values.pageSize}
            onChange={(e) => onChange(index, "pageSize", e.target.value)}
            placeholder="45-55"
            error={!!errors?.pageSize}
            helperText={errors?.pageSize}
            fullWidth
          />

          <TextField
            label="Count*"
            type="number"
            value={values.count}
            onChange={(e) => onChange(index, "count", parseInt(e.target.value))}
            error={!!errors?.count}
            helperText={errors?.count}
            fullWidth
          />
        </Stack>

        <Stack direction="row" gap={2} alignItems="center">
          <Typography>Use master combination</Typography>
          <Switch
            checked={values.useMasterCombination}
            onChange={(e) =>
              onChange(index, "useMasterCombination", e.target.checked)
            }
          />
        </Stack>

        {values.useMasterCombination && (
          <Stack direction="row" gap={2}>
            <StoryFormDropdown
              label="Master combination*"
              value={values.masterCombinationName}
              options={
                combinations?.items
                  .filter(
                    (c) =>
                      c.storyConfigRevision.storyConfig.storyType ===
                      values.storyType,
                  )
                  .map((combo) => ({
                    value: combo.name,
                    label: combo.name,
                  })) || []
              }
              onChange={(value) =>
                onChange(index, "masterCombinationName", value)
              }
              error={errors?.masterCombinationName}
              disabled={!values.storyType}
            />
             <StoryFormDropdown
                label="LLM model name"
                value={values.modelName}
                options={Object.values(ModelOption)}
                onChange={(value) => onChange(index, "modelName", value)}
                error={errors?.modelName}
              />
          </Stack>
        )}

        {!values.useMasterCombination && (
          <>
            <Stack direction="row" gap={2}>
              <StoryFormDropdown
                label="LLM model name"
                value={values.modelName}
                options={Object.values(ModelOption)}
                onChange={(value) => onChange(index, "modelName", value)}
                error={errors?.modelName}
              />

              <StoryFormDropdown
                label="Story config revision"
                value={values.configRevision}
                options={
                  values.storyType && configs?.items
                    ? (
                        configs.items
                          .find((c) => c.storyType === values.storyType)
                          ?.revisions.map((r) => r.revision) ?? []
                      ).toSorted((a, b) => a - b)
                    : []
                }
                onChange={(value) => onChange(index, "configRevision", value)}
                error={errors?.configRevision}
                disabled={!values.storyType}
              />
            </Stack>

            <Stack direction="row" gap={2}>
              <StoryFormDropdown
                label="Text prompt name*"
                value={values.promptName}
                options={
                  values.storyType && promptsByStoryType[values.storyType]
                    ? promptsByStoryType[values.storyType]
                        .filter((prompt) => prompt.type === PromptType.text)
                        .map((prompt) => prompt.name)
                    : []
                }
                onChange={(value) => onChange(index, "promptName", value)}
                error={errors?.promptName}
                disabled={!values.storyType}
              />

              <StoryFormDropdown
                label="Text prompt revision*"
                value={values.promptRevision}
                options={
                  textPrompt
                    ? textPrompt.revisions
                        .map((rev) => rev.revision)
                        .toSorted((a, b) => a - b)
                    : []
                }
                onChange={(value) => onChange(index, "promptRevision", value)}
                error={errors?.promptRevision}
                disabled={!textPrompt}
              />

              <StoryFormDropdown
                label="Image prompt name"
                value={values.imagePromptName}
                options={
                  values.storyType && promptsByStoryType[values.storyType]
                    ? promptsByStoryType[values.storyType]
                        .filter((prompt) => prompt.type === PromptType.image)
                        .map((prompt) => prompt.name)
                    : []
                }
                onChange={(value) => onChange(index, "imagePromptName", value)}
                error={errors?.imagePromptName}
                disabled={!values.storyType}
              />

              <StoryFormDropdown
                label="Image prompt revision"
                value={values.imagePromptRevision}
                options={
                  imagePrompt
                    ? imagePrompt.revisions
                        .map((rev) => rev.revision)
                        .toSorted((a, b) => a - b)
                    : []
                }
                onChange={(value) =>
                  onChange(index, "imagePromptRevision", value)
                }
                error={errors?.imagePromptRevision}
                disabled={!imagePrompt}
              />
            </Stack>
          </>
        )}
      </Stack>
    </Paper>
  );
}

type DropdownProps = {
  label: string;
  value: string | number | undefined;
  options:
    | readonly (string | number)[]
    | readonly { value: string | number; label: string }[];
  disabled?: boolean;
  error?: string;
  onChange: (value: string | number) => void;
};

function StoryFormDropdown({
  label,
  value,
  options,
  disabled,
  error,
  onChange,
}: DropdownProps) {
  return (
    <TextField
      label={label}
      value={value || ""}
      error={!!error}
      helperText={error}
      disabled={disabled}
      select
      onChange={(e) => onChange(e.target.value)}
      fullWidth
    >
      {options.map((option) =>
        typeof option === "object" ? (
          <MenuItem key={option.value} value={option.value}>
            {option.label}
          </MenuItem>
        ) : (
          <MenuItem key={option} value={option}>
            {option}
          </MenuItem>
        ),
      )}
    </TextField>
  );
}
